import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { KeyRound, Mail, CheckCircle } from "lucide-react";

// Password Reset Request Dialog Component
export const PasswordResetRequestDialog = ({ isOpen, onClose, API }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email: email.toLowerCase().trim() });
      setSuccess(true);
    } catch (err) {
      // Always show success message for security (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setSuccess(false);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-400" />
            Reset Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {success 
              ? "Check your email for reset instructions"
              : "Enter your email to receive a password reset link"
            }
          </DialogDescription>
        </DialogHeader>
        
        {success ? (
          <div className="space-y-4">
            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-green-300 font-medium">Reset Link Sent!</p>
              <p className="text-slate-400 text-sm mt-2">
                If an account exists for <span className="text-white">{email}</span>, you&apos;ll receive an email with instructions to reset your password.
              </p>
            </div>
            <p className="text-slate-500 text-xs text-center">
              Didn&apos;t receive the email? Check your spam folder or try again.
            </p>
            <Button onClick={handleClose} className="w-full bg-blue-600 hover:bg-blue-700">
              Back to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reset-email" className="text-slate-200">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="reset-email-input"
              />
            </div>
            {error && (
              <div className="text-red-400 text-sm bg-red-500/20 border border-red-500/30 p-2 rounded">{error}</div>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-submit-btn">
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Password Reset Confirmation Dialog Component
export const PasswordResetConfirmDialog = ({ isOpen, onClose, token, API, onSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: newPassword 
      });
      setSuccess(true);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (errorDetail === "Invalid or expired reset token") {
        setError("This reset link has expired or is invalid. Please request a new one.");
      } else {
        setError(errorDetail || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    setSuccess(false);
    setError("");
    onClose();
    if (success && onSuccess) {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-400" />
            Set New Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {success 
              ? "Your password has been updated"
              : "Enter your new password below"
            }
          </DialogDescription>
        </DialogHeader>
        
        {success ? (
          <div className="space-y-4">
            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-green-300 font-medium">Password Reset Successful!</p>
              <p className="text-slate-400 text-sm mt-2">
                Your password has been updated. You can now log in with your new password.
              </p>
            </div>
            <Button onClick={handleClose} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="reset-success-login-btn">
              Go to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-slate-200">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-slate-200">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="confirm-password-input"
              />
            </div>
            {error && (
              <div className="text-red-400 text-sm bg-red-500/20 border border-red-500/30 p-2 rounded">{error}</div>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="set-password-btn">
                {loading ? "Updating..." : "Set New Password"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
